/**
 * Canonical widget settings row used by dashboards and admin UI.
 */

import {
  ALL_WIDGETS,
  DASHBOARD_WIDGET_IDS_OFFERED_BY_ROLE,
  DashboardWidgetId,
  type DashboardWidgetSettings,
  DEFAULT_DASHBOARD_SETTINGS,
  RETIRED_DASHBOARD_WIDGET_IDS,
  type UserRole,
} from "@/lib/types/dashboard-widgets";
import type {
  WidgetDataItemFromApi,
  WidgetSettingCategoryDto,
  WidgetSettingFromApi,
} from "./widgets-client";

const ALL_USER_ROLES: UserRole[] = ["Admin", "Teacher", "Staff"];

const ENUM_VALUES = new Set(Object.values<string>(DashboardWidgetId));

export interface NormalizedWidgetSettingRow {
  settingId: number;
  widgetId: DashboardWidgetId;
  /** Original key from API (equals widget slug). */
  key: string;
  name: string;
  displayName: string | null;
  category: WidgetSettingCategoryDto;
  rolesForWidget: UserRole[];
  /**
   * Exact `roles` strings last seen from GET for this row; use on PUT so casing
   * matches what the API expects.
   */
  apiTokensByRole: Partial<Record<UserRole, string>>;
  /** From API `sortOrder` (legacy: `displayOrder`); fallback when a role has no own order. */
  apiOrderIndex: number;
  /** Display order for each portal. Missing roles fall back to `apiOrderIndex`. */
  orderByRole: Partial<Record<UserRole, number>>;
}

function parseWidgetKey(key: string): DashboardWidgetId | null {
  if (!key || typeof key !== "string") return null;
  const normalized =
    key === "enrollment-calendar" ? DashboardWidgetId.EnrollmentsCalendar : key;
  return ENUM_VALUES.has(normalized) ? (normalized as DashboardWidgetId) : null;
}

function normalizeRoleToken(token: string): UserRole | null {
  const lower = token.trim().toLowerCase();
  if (!lower) return null;
  if (lower === "admin" || lower === "administrator") return "Admin";
  if (lower === "teacher" || lower === "instructor") return "Teacher";
  if (lower === "staff") return "Staff";
  return null;
}

export function rolesFromApiTokens(tokens: string[] | undefined): UserRole[] {
  if (!tokens?.length) return [];
  const out = new Set<UserRole>();
  for (const r of tokens) {
    const n = normalizeRoleToken(r);
    if (n) out.add(n);
  }
  return ALL_USER_ROLES.filter((role) => out.has(role));
}

/** Default role token on PUT when GET never included that role for this widget (C# style). */
function defaultApiRoleToken(role: UserRole): string {
  return role;
}

/**
 * Build `widgetRoles` array for PUT: reuse GET token per role when known, else PascalCase default.
 */
export function widgetRolesForPut(
  roles: UserRole[],
  apiTokensByRole: Partial<Record<UserRole, string>>,
): string[] {
  const out: string[] = [];
  for (const role of ALL_USER_ROLES) {
    if (!roles.includes(role)) continue;
    const fromGet = apiTokensByRole[role];
    out.push(
      fromGet !== undefined && fromGet !== ""
        ? fromGet
        : defaultApiRoleToken(role),
    );
  }
  return out;
}

/** KPI vs Regular is fixed in the app catalog, not editable per tenant row. PUT still sends category so the backend stays aligned. */
function widgetCategoryFromCatalog(
  widgetId: DashboardWidgetId,
): WidgetSettingCategoryDto {
  const cfg = ALL_WIDGETS.find((w) => w.id === widgetId);
  return cfg?.category === "kpi" ? "Kpi" : "Regular";
}

function apiTokensByRoleFromStrings(
  tokens: string[] | undefined,
): Partial<Record<UserRole, string>> {
  const map: Partial<Record<UserRole, string>> = {};
  for (const tok of tokens ?? []) {
    const logical = normalizeRoleToken(tok);
    if (!logical) continue;
    if (map[logical] !== undefined) continue;
    map[logical] = tok.trim();
  }
  return map;
}

function coerceApiOrderValue(raw: unknown): number | null {
  if (raw == null) return null;
  if (typeof raw === "number" && Number.isFinite(raw)) return Math.trunc(raw);
  if (typeof raw === "string") {
    const t = raw.trim();
    if (/^-?(?:0|[1-9]\d*)$/.test(t)) return parseInt(t, 10);
  }
  return null;
}

function readRoleOrdersFromApiRow(
  row: WidgetSettingFromApi,
): Partial<Record<UserRole, number>> {
  const raw = row.roleOrders ?? row.RoleOrders;
  if (!Array.isArray(raw)) return {};
  const out: Partial<Record<UserRole, number>> = {};
  for (const item of raw) {
    const role = normalizeRoleToken(item.role ?? item.Role ?? "");
    const order =
      coerceApiOrderValue(item.sortOrder) ?? coerceApiOrderValue(item.SortOrder);
    if (!role || order == null) continue;
    out[role] = order;
  }
  return out;
}

export function orderIndexForRole(
  row: NormalizedWidgetSettingRow,
  role: UserRole,
): number {
  return row.orderByRole[role] ?? row.apiOrderIndex;
}

export function roleOrdersEqual(
  a: Partial<Record<UserRole, number>> | undefined,
  b: Partial<Record<UserRole, number>> | undefined,
): boolean {
  return ALL_USER_ROLES.every(
    (role) => (a?.[role] ?? null) === (b?.[role] ?? null),
  );
}

function readDisplayOrderFromApiRow(
  row: WidgetSettingFromApi,
  fallback: number,
): number {
  const fromSort =
    coerceApiOrderValue(row.sortOrder) ?? coerceApiOrderValue(row.SortOrder);
  if (fromSort != null) return fromSort;

  const fromLegacy =
    typeof row.displayOrder === "number" && !Number.isNaN(row.displayOrder)
      ? Math.trunc(row.displayOrder)
      : typeof row.DisplayOrder === "number" && !Number.isNaN(row.DisplayOrder)
        ? Math.trunc(row.DisplayOrder)
        : null;
  return fromLegacy != null ? fromLegacy : fallback;
}

export function normalizeWidgetSettingsPayload(
  items: WidgetSettingFromApi[],
): NormalizedWidgetSettingRow[] {
  const out: NormalizedWidgetSettingRow[] = [];
  items.forEach((row, idx) => {
    const widgetId = parseWidgetKey(row.key ?? "");
    if (widgetId == null) return;
    // Use `roles` when present even if []; otherwise stale `widgetRoles` would win.
    const roleTokens = Array.isArray(row.roles)
      ? row.roles
      : Array.isArray(row.widgetRoles)
        ? row.widgetRoles
        : [];
    const rolesForWidget = rolesFromApiTokens(roleTokens);
    const apiTokensByRole = apiTokensByRoleFromStrings(roleTokens);
    out.push({
      settingId: Number(row.id),
      widgetId,
      key: row.key,
      name: typeof row.name === "string" ? row.name : String(row.key),
      displayName: row.displayName ?? null,
      category: widgetCategoryFromCatalog(widgetId),
      rolesForWidget,
      apiTokensByRole,
      apiOrderIndex: readDisplayOrderFromApiRow(row, idx),
      orderByRole: readRoleOrdersFromApiRow(row),
    });
  });
  return out.sort((a, b) => {
    if (a.apiOrderIndex !== b.apiOrderIndex)
      return a.apiOrderIndex - b.apiOrderIndex;
    const cat = String(a.category).localeCompare(String(b.category));
    if (cat !== 0) return cat;
    return a.settingId - b.settingId;
  });
}

/** Layout for a dashboard role derived from normalized API rows (+ catalog constraints). */
export function layoutForRoleFromNormalized(
  normalized: NormalizedWidgetSettingRow[],
  role: UserRole,
): { kpiWidgets: DashboardWidgetId[]; regularWidgets: DashboardWidgetId[] } {
  const offered = new Set(DASHBOARD_WIDGET_IDS_OFFERED_BY_ROLE[role]);
  const kpis = normalized
    .filter(
      (r) =>
        r.category === "Kpi" &&
        r.rolesForWidget.includes(role) &&
        offered.has(r.widgetId) &&
        !RETIRED_DASHBOARD_WIDGET_IDS.has(r.widgetId),
    )
    .sort((a, b) => orderIndexForRole(a, role) - orderIndexForRole(b, role))
    .map((r) => r.widgetId);

  const regularSource = normalized
    .filter((r) => r.category === "Regular" && r.rolesForWidget.includes(role))
    .sort((a, b) => orderIndexForRole(a, role) - orderIndexForRole(b, role));

  return {
    kpiWidgets: kpis,
    regularWidgets: migrateRetiredRegularWidgets(regularSource, offered),
  };
}

function migrateRetiredRegularWidgets(
  source: NormalizedWidgetSettingRow[],
  offered: ReadonlySet<DashboardWidgetId>,
): DashboardWidgetId[] {
  const out: DashboardWidgetId[] = [];
  const replacement = DashboardWidgetId.RecentActivity;
  const canReplace = offered.has(replacement);

  for (const row of source) {
    if (row.widgetId === DashboardWidgetId.NewEnrollments) {
      if (canReplace && !out.includes(replacement)) {
        out.push(replacement);
      }
      continue;
    }
    if (!offered.has(row.widgetId)) continue;
    if (RETIRED_DASHBOARD_WIDGET_IDS.has(row.widgetId)) continue;
    if (out.includes(row.widgetId)) continue;
    out.push(row.widgetId);
  }

  return out;
}

export function foldNewEnrollmentsRolesForDisplay(
  row: NormalizedWidgetSettingRow,
  rowsByWidgetId: ReadonlyMap<DashboardWidgetId, NormalizedWidgetSettingRow>,
): NormalizedWidgetSettingRow {
  if (row.widgetId !== DashboardWidgetId.RecentActivity) return row;
  const retired = rowsByWidgetId.get(DashboardWidgetId.NewEnrollments);
  if (!retired?.rolesForWidget.length) return row;
  const roles = new Set<UserRole>([
    ...row.rolesForWidget,
    ...retired.rolesForWidget,
  ]);
  return {
    ...row,
    rolesForWidget: ALL_USER_ROLES.filter((role) => roles.has(role)),
  };
}

/** Keep Enrollment Status Changes as the only enrollment-list toggle; retire New Enrollments roles. */
export function applyEnrollmentStatusChangesRoleToggle(
  rows: NormalizedWidgetSettingRow[],
  role: UserRole,
  nextChecked: boolean,
): NormalizedWidgetSettingRow[] {
  return rows.map((row) => {
    if (row.widgetId === DashboardWidgetId.NewEnrollments) {
      return {
        ...row,
        rolesForWidget: row.rolesForWidget.filter((item) => item !== role),
      };
    }
    if (row.widgetId !== DashboardWidgetId.RecentActivity) return row;
    const hasRole = row.rolesForWidget.includes(role);
    if (nextChecked) {
      return hasRole
        ? row
        : { ...row, rolesForWidget: [...row.rolesForWidget, role] };
    }
    return hasRole
      ? {
          ...row,
          rolesForWidget: row.rolesForWidget.filter((item) => item !== role),
        }
      : row;
  });
}

/** Full DashboardWidgetSettings; falls back when API yields nothing for a role. */
export function buildDashboardWidgetSettings(
  normalized: NormalizedWidgetSettingRow[],
): DashboardWidgetSettings {
  if (normalized.length === 0) {
    return { ...DEFAULT_DASHBOARD_SETTINGS };
  }
  return {
    Admin: layoutForRoleFromNormalized(normalized, "Admin"),
    Teacher: layoutForRoleFromNormalized(normalized, "Teacher"),
    Staff: layoutForRoleFromNormalized(normalized, "Staff"),
  };
}

/**
 * Applies a new order for one portal inside one category.
 * Other portals keep their own order.
 */
export function applyRoleCategoryDisplayOrderPatch(
  rows: NormalizedWidgetSettingRow[],
  role: UserRole,
  category: WidgetSettingCategoryDto,
  orderedWidgetIds: DashboardWidgetId[],
): NormalizedWidgetSettingRow[] {
  const positions = new Map(
    orderedWidgetIds.map((id, index) => [id as string, index]),
  );
  return rows.map((row) => {
    if (row.category !== category) return row;
    const next = positions.get(row.widgetId);
    if (next === undefined) return row;
    return {
      ...row,
      orderByRole: { ...row.orderByRole, [role]: next },
    };
  });
}

export function applyWidgetDataToKpiData(
  kpiWidgets: DashboardWidgetId[],
  dataItems: WidgetDataItemFromApi[],
  base: {
    totalStudents?: number;
    activeStudents?: number;
    totalClasses?: number;
    activeClasses?: number;
    pendingEnrollments?: number;
    enrolledStudents?: number;
    activeEnrollments?: number;
    totalTeachers?: number;
    activeTeachers?: number;
    teachersWithClasses?: number;
    totalSeasons?: number;
    activeSeasons?: number;
    totalLocations?: number;
    activeLocations?: number;
    totalClassrooms?: number;
    activeClassrooms?: number;
    totalPolicies?: number;
    activePolicies?: number;
  },
): typeof base {
  const allow = new Set(kpiWidgets);
  const merged = { ...base };

  const takeTotal = (
    widgetId: DashboardWidgetId,
    field: keyof typeof merged,
    item?: WidgetDataItemFromApi,
  ) => {
    if (!allow.has(widgetId)) return;
    if (!item?.data || item.data.$type !== "count") return;
    if (typeof item.data.total !== "number") return;
    merged[field] = item.data.total;
  };

  for (const row of dataItems) {
    const id = parseWidgetKey(row.key);
    if (!id) continue;
    switch (id) {
      // Enrolled Students (`total-students`) is unique currently-enrolled
      // students from FE enrollments, not the widget aggregate count.
      // TotalClasses: scroll `totalCount` is authoritative (widget may return
      // active-only or stale counts).
      case DashboardWidgetId.ActiveEnrollments:
        takeTotal(id, "activeEnrollments", row);
        break;
      case DashboardWidgetId.TotalTeachers:
        takeTotal(id, "totalTeachers", row);
        break;
      case DashboardWidgetId.TotalSeasons:
        takeTotal(id, "totalSeasons", row);
        break;
      case DashboardWidgetId.TotalLocations:
        takeTotal(id, "totalLocations", row);
        break;
      case DashboardWidgetId.TotalClassrooms:
        takeTotal(id, "totalClassrooms", row);
        break;
      case DashboardWidgetId.TotalPolicies:
        takeTotal(id, "totalPolicies", row);
        break;
      default:
        break;
    }
  }

  return merged;
}
